import { forwardRef, useEffect, useImperativeHandle, useRef, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useFieldControl } from './Field';
import { Spinner } from './Spinner';

/** Shared control chrome: platinum hairline border, sapphire focus ring, sunken disabled state. */
export const controlClasses =
  'w-full rounded-lg border border-control bg-surface text-ink placeholder:text-ink-subtle shadow-[inset_0_1px_1px_rgb(10_15_34/0.03)] ' +
  'transition duration-150 hover:border-sapphire-400 ' +
  'focus-visible:border-sapphire-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sapphire-500/40 focus-visible:ring-offset-0 ' +
  'dark:focus-visible:border-sapphire-400 dark:focus-visible:ring-sapphire-400/40 ' +
  'disabled:cursor-not-allowed disabled:bg-sunken disabled:text-ink-subtle disabled:hover:border-control ' +
  'aria-[invalid=true]:border-danger-600 aria-[invalid=true]:focus-visible:ring-danger-500/30 [&:read-only:not(select)]:bg-sunken';

const controlSizes = { sm: 'h-8 px-2.5 text-sm', md: 'h-10 px-3 text-sm', lg: 'h-12 px-4 text-base' } as const;
export type ControlSize = keyof typeof controlSizes;

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size?: ControlSize;
  leftIcon?: ReactNode;
  /** Content inside the right edge (units, a button). */
  rightSlot?: ReactNode;
  mono?: boolean;
  wrapperClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { size = 'md', leftIcon, rightSlot, mono, className, wrapperClassName, ...rest },
  ref,
) {
  const field = useFieldControl({ id: rest.id, 'aria-describedby': rest['aria-describedby'], 'aria-invalid': rest['aria-invalid'], required: rest.required });
  return (
    <div className={cn('relative', wrapperClassName)}>
      {leftIcon && <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-ink-subtle [&>svg]:h-4 [&>svg]:w-4">{leftIcon}</span>}
      <input
        ref={ref}
        {...rest}
        id={field.id}
        aria-describedby={field['aria-describedby']}
        aria-invalid={field['aria-invalid']}
        required={field.required}
        className={cn(controlClasses, controlSizes[size], leftIcon && 'pl-9', rightSlot && 'pr-10', mono && 'font-mono tracking-wide', className)}
      />
      {rightSlot && <span className="absolute inset-y-0 right-2 flex items-center">{rightSlot}</span>}
    </div>
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Grow with content up to this many rows. */
  autoGrow?: number;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea({ className, autoGrow, rows = 3, onInput, ...rest }, ref) {
  const field = useFieldControl({ id: rest.id, 'aria-describedby': rest['aria-describedby'], 'aria-invalid': rest['aria-invalid'], required: rest.required });
  const inner = useRef<HTMLTextAreaElement>(null);
  useImperativeHandle(ref, () => inner.current as HTMLTextAreaElement);
  const resize = (): void => {
    const el = inner.current;
    if (!el || !autoGrow) {
      return;
    }
    el.style.height = 'auto';
    const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 20;
    el.style.height = `${Math.min(el.scrollHeight, lineHeight * autoGrow + 16)}px`;
  };
  useEffect(resize);
  return (
    <textarea
      ref={inner}
      rows={rows}
      {...rest}
      id={field.id}
      aria-describedby={field['aria-describedby']}
      aria-invalid={field['aria-invalid']}
      required={field.required}
      onInput={(event) => {
        resize();
        onInput?.(event);
      }}
      className={cn(controlClasses, 'min-h-[5rem] resize-y px-3 py-2 text-sm', className)}
    />
  );
});

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  size?: ControlSize;
  wrapperClassName?: string;
}

/** Native <select> with a custom chevron: best accessibility and mobile behaviour. */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select({ size = 'md', className, wrapperClassName, children, ...rest }, ref) {
  const field = useFieldControl({ id: rest.id, 'aria-describedby': rest['aria-describedby'], 'aria-invalid': rest['aria-invalid'], required: rest.required });
  return (
    <div className={cn('relative', wrapperClassName)}>
      <select
        ref={ref}
        {...rest}
        id={field.id}
        aria-describedby={field['aria-describedby']}
        aria-invalid={field['aria-invalid']}
        required={field.required}
        className={cn(controlClasses, controlSizes[size], 'appearance-none pr-9', className)}
      >
        {children}
      </select>
      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtle" aria-hidden="true" />
    </div>
  );
});

export interface SearchInputProps extends Omit<InputProps, 'leftIcon' | 'rightSlot' | 'type' | 'onChange' | 'value'> {
  value: string;
  onValueChange: (value: string) => void;
  loading?: boolean;
  onClear?: () => void;
  /** Hint shown as a keyboard badge when empty (e.g. "/"). */
  shortcutHint?: string;
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
  { value, onValueChange, loading, onClear, shortcutHint, placeholder = 'Search', 'aria-label': ariaLabel, className, size = 'md', wrapperClassName, mono: _mono, ...rest },
  ref,
) {
  return (
    <div className={cn('relative', wrapperClassName)}>
      <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-ink-subtle">
        <Search className="h-4 w-4" aria-hidden="true" />
      </span>
      <input
        ref={ref}
        type="search"
        value={value}
        placeholder={placeholder}
        aria-label={ariaLabel ?? placeholder}
        onChange={(event) => onValueChange(event.target.value)}
        className={cn(controlClasses, controlSizes[size], 'pl-9 pr-9 [&::-webkit-search-cancel-button]:hidden', className)}
        {...rest}
      />
      <span className="absolute inset-y-0 right-2 flex items-center">
        {loading ? (
          <Spinner size="sm" className="text-ink-subtle" label="Searching" />
        ) : value ? (
          <button
            type="button"
            aria-label="Clear search"
            className="rounded-md p-1 text-ink-subtle hover:bg-sunken hover:text-ink"
            onClick={() => {
              onValueChange('');
              onClear?.();
            }}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        ) : shortcutHint ? (
          <kbd className="rounded border border-line-strong bg-sunken px-1.5 font-mono text-2xs text-ink-muted">{shortcutHint}</kbd>
        ) : null}
      </span>
    </div>
  );
});
